/**
 * jarvis-today PH-02: the words Today may show (the screen's copy of the console's server/today_words.js, so an
 * older server's words cannot put a code on screen). Ruling (a), business lead -10751 Oct 28: no access code, gate
 * code or password-shaped text ever renders on Today, and a reason line is a reason of at most about 120 characters,
 * never the agent's brief. Text is never cut token by token (lesson L-0660): a line that is too long, more than one
 * sentence, or carries a code is replaced whole.
 */

export const REASON_MAX = 120;
export const GUARD_LINE = 'Has an access code: open the record to read it.';

const CODE_VALUE = new RegExp(
  '(?<!\\b(?:zip|area|postal|country|status|error|exit|source|qr|promo|discount|indiana|municipal|city|tax|fire'
  + '|zoning|penal|civil|ordinance|building|ic)\\s)'
  + '\\b(?:code|combo|combination|pin|passcode|password|pw|pwd)\\b\\s*(?:is\\s+|was\\s+|=\\s*|:\\s*|#\\s*|-\\s*)?'
  + '["\'\\u201c]?[#*]?(?=[\\w#*-]*\\d)[\\w#*-]{3,16}', 'i');
// "password: x", "passcode is x", "pw: x": any value (PH-02c: a one-letter value too).
const PASSWORD = /\b(?:password|passcode|pwd?)\s*(?:is|=|:)\s*\S+/i;
// PH-02d: "pass: Hunter22", "pass = x": only with a colon or equals sign ("pass the report" is a verb).
const PASS_MARKED = /\bpass\s*[:=]\s*\S+/i;
const ACCESS = /\b(?:lock\s*-?\s*box(?:es)?|gate|garage|door|alarm|access|entry|key\s*-?\s*pad|supra|cbs|combo)\s+(?:code|combo|combination|pin|passcode|password)s?\b/i;
// PH-02b: a device holding a code, then a value with a digit, with no "code" word ("Lockbox: 4821", "Supra 2468").
const DEVICE_VALUE = /\b(?:lock\s*-?\s*box(?:es)?|key\s*-?\s*(?:box|pad)|supra|cbs)\b\s*(?:is\s+|was\s+|=\s*|:\s*|#\s*|-\s*)?["'\u201c]?[#*]?(?=[\w#*-]*\d)[\w#*-]{3,16}/i;
// PH-02b: a gate, garage, door or alarm needs an explicit marker before the value ("Gate: 7733", not "Door: 3 panel").
const MARKED_VALUE = /\b(?:gate|garage|door|alarm|entry)\s*(?:is\s+|=\s*|:\s*|#\s*)["'\u201c]?[#*]?(?=[\w#*-]*\d)[\w#*-]{3,16}/i;
// PH-02b: a password followed by a bare word ("password huntersecret"), unless the word talks about the password.
const PASSWORD_WORD = /\b(?:password|passcode|pwd?)\s+(?!(?:is|was|for|to|of|on|in|at|and|or|the|a|an|from|with|via|it|that|this|again|has|had|will|can|manager|link|email|login|protected|expired|field|hint|policy|reset\w*|change\w*|update\w*|recover\w*|request\w*|sent|send)\b)["'\u201c]?\S{3,}/i;
// PH-03: dotted letters ("e.g.", "p.m.", "N.") and, any case, the short words a listing note uses ("sq. ft.").
const ABBREVIATION = /(?:^|\s)(?:(?:[A-Za-z]\.)+|(?:St|Mr|Mrs|Ms|Dr|Ave|Rd|Ln|Ct|Blvd|No|Jr|Sr|Inc|Co|Mt|Ft|vs|etc|approx|sq|est|incl|apt|ste|dept|appt|ext|min|max|hrs?|wk|mo|yrs?)\.)$/i;
// PH-03: a link or an email address is public text, never a code ("?code=4821", "code-4821@example.com"). They are
// blanked only for the test; the text shown is never changed (L-0660).
const LINK_OR_EMAIL = /(?:\bhttps?:\/\/|\bwww\.)\S+|[\w.+-]+@[\w-]+(?:\.[\w-]+)+/gi;

export function hasAccessCode(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  const text = value.replace(LINK_OR_EMAIL, ' ');
  return CODE_VALUE.test(text) || PASSWORD.test(text) || ACCESS.test(text) || DEVICE_VALUE.test(text)
    || MARKED_VALUE.test(text) || PASSWORD_WORD.test(text) || PASS_MARKED.test(text);
}

function sentences(value: string): number {
  let count = 1;
  // PH-03: a lowercase word after the stop starts a sentence too ("Send it. then call."); abbreviations still do not.
  const boundary = /[.!?]+\s+(?=["'“(]?[A-Za-z0-9])/g;
  let match: RegExpExecArray | null;
  while ((match = boundary.exec(value)) !== null) {
    if (!ABBREVIATION.test(value.slice(0, match.index + 1))) count += 1;
  }
  return count;
}

/** The text as a reason (whitespace folded) when short, one sentence and code-free; else the fallback, never cut. */
export function reasonText<T extends string | null>(value: unknown, fallback: T): string | T {
  if (typeof value !== 'string') return fallback;
  const cleaned = value.replace(/\s+/g, ' ').trim();
  if (!cleaned) return fallback;
  if (cleaned.length > REASON_MAX || /[\n•]/.test(value) || sentences(cleaned) > 1 || hasAccessCode(cleaned)) {
    return fallback;
  }
  return cleaned;
}

/** Every string in a response that still carries a code becomes the fixed line (a copy; the input is kept). */
export function guardBody<T>(value: T): T {
  if (typeof value === 'string') return (hasAccessCode(value) ? GUARD_LINE : value) as T;
  if (Array.isArray(value)) return value.map((item) => guardBody(item)) as T;
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value as Record<string, unknown>)) out[key] = guardBody(item);
    return out as T;
  }
  return value;
}

/**
 * A due need's "Name: what to do". What to do is a short reason; a call whose task is too long or carries a code keeps
 * its first part ("Call Ann Smith") so Next up still finds it; a part is never written twice ("Follow-up: Follow-up").
 */
export function dueLabel(label: string): string {
  const split = label.indexOf(': ');
  const subject = split > 0 ? label.slice(0, split) : null;
  const rest = split > 0 ? label.slice(split + 2) : label;
  let task = reasonText(rest, null);
  if (task === null && /^Call\s/.test(rest)) task = reasonText(rest.split(':')[0], null);
  // PH-03: no shorter first part to show (a code or a long task, no colon): the call still reads as a call.
  if (task === null && /^Call\s/.test(rest) && subject !== null) task = `Call ${subject}`;
  if (subject === null) return task ?? (hasAccessCode(rest) ? GUARD_LINE : 'A task is due');
  return task === null || task === subject ? subject : `${subject}: ${task}`;
}
